import { useLoader } from "@/context/LoaderContext";
import { useUser } from "@/context/UserContext";
import { getCompletedCourses } from "@/services/courses";
import { useEffect, useMemo } from "react";
import { useQuery } from "react-query";
type UseGetCompletedCoursesOptions = {
  /** When true, skips Nest `completed-courses` (caller supplies counts elsewhere, e.g. PDC list on /courses). */
  disableQuery?: boolean;
};

export function useGetCompletedCourses(options?: UseGetCompletedCoursesOptions) {
  const loader = useLoader();
  const { userInfo } = useUser();
  const skipQuery = Boolean(options?.disableQuery);

  const { data, isLoading } = useQuery(
    "completed-courses",
    () => getCompletedCourses(),
    { enabled: !!userInfo?.id && !skipQuery }
  );

  const coursesWithProgressCount = useMemo(() => {
    if (skipQuery) return 0;
    if (isLoading) return 0;
    return data?.coursesWithProgress ?? data?.completedCourses ?? 0;
  }, [skipQuery, isLoading, data]);

  const totalCoursesCount = useMemo(() => {
    if (skipQuery) return 0;
    return data?.totalCourses ?? 0;
  }, [skipQuery, data?.totalCourses]);

  const completedCoursesCount = useMemo(() => {
    if (skipQuery) return 0;
    if (isLoading) return 0;
    return data?.completedCourses ?? 0;
  }, [skipQuery, isLoading, data?.completedCourses]);

  useEffect(() => {
    if (skipQuery) {
      loader.hide("get-completed-courses");
      return;
    }
    if (isLoading) {
      loader.show("get-completed-courses");
    } else {
      loader.hide("get-completed-courses");
    }
    return () => {
      loader.hide("get-completed-courses");
    };
  }, [skipQuery, isLoading, loader]);

  return {
    coursesWithProgressCount,
    totalCoursesCount,
    completedCoursesCount,
    isLoading,
  };
}
